/**
 * idbPersister - IndexedDB-backed async storage for the TanStack Query cache.
 *
 * The TanStack `createAsyncStoragePersister` accepts any object that implements
 * the `getItem` / `setItem` / `removeItem` async-storage contract. We back that
 * contract with IndexedDB (via the `idb` library, already a project dependency)
 * so the serialized query cache survives reloads and is available offline.
 *
 * A DEDICATED database (`gb-query-cache`) is used rather than the offline
 * write-queue database (`gb-offline-db`, see services/offlineStorage.ts). Keeping
 * them separate avoids version/upgrade collisions between the two subsystems:
 *	 - gb-offline-db  -> user-authored writes awaiting sync (source of truth)
 *	 - gb-query-cache -> a disposable mirror of server reads (safe to clear)
 *
 * Only ONE key is ever written (`REACT_QUERY_OFFLINE_CACHE`) because the
 * persist-client serializes the whole cache into a single blob.
 */
import { openDB, IDBPDatabase } from 'idb';

/**
 * Minimal async key/value contract expected by createAsyncStoragePersister
 * (mirrors TanStack's internal `AsyncStorage<string>`). Defined locally so we
 * don't import from the persist-client-core internal package.
 */
interface AsyncStorage<T = string> {
	getItem: (key: string) => Promise<T | null>;
	setItem: (key: string, value: T) => Promise<unknown>;
	removeItem: (key: string) => Promise<void>;
}

/** Dedicated DB + store for the persisted query cache (kept apart from the write-queue). */
const DB_NAME = 'gb-query-cache';
const STORE_NAME = 'queryCache';
const DB_VERSION = 1;

/** Cached connection so we don't re-open IndexedDB on every read/write. */
let dbInstance: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
	if (dbInstance) return dbInstance;
	dbInstance = await openDB(DB_NAME, DB_VERSION, {
		upgrade(db) {
			if (!db.objectStoreNames.contains(STORE_NAME)) {
				// Simple key/value store — the persister manages a single blob key.
				db.createObjectStore(STORE_NAME);
			}
		},
	});
	return dbInstance;
}

/**
 * AsyncStorage implementation backed by IndexedDB. All three methods swallow
 * errors (returning null / no-op) so that storage being unavailable — private
 * browsing, quota exceeded — degrades to an in-memory cache instead of crashing
 * the app shell.
 */
export const idbStorage: AsyncStorage<string> = {
	async getItem(key: string): Promise<string | null> {
		try {
			const db = await getDb();
			const value = await db.get(STORE_NAME, key);
			return (value as string | undefined) ?? null;
		} catch (error) {
			console.error('[idbPersister] getItem failed:', error);
			return null;
		}
	},
	async setItem(key: string, value: string): Promise<void> {
		try {
			const db = await getDb();
			await db.put(STORE_NAME, value, key);
		} catch (error) {
			console.error('[idbPersister] setItem failed:', error);
		}
	},
	async removeItem(key: string): Promise<void> {
		try {
			const db = await getDb();
			await db.delete(STORE_NAME, key);
		} catch (error) {
			console.error('[idbPersister] removeItem failed:', error);
		}
	},
};
